import { type Href, useGlobalSearchParams, usePathname } from "expo-router";
import { useCallback, useEffect, useMemo } from "react";

import {
  PartnerIntent,
  type PartnerIntentRequest,
} from "@/api/generated/models";
import asyncStorage from "@/asyncStorage";
import { addBreadcrumb, captureMessage } from "@/sentry";
import { type QueryParam, firstQueryParam } from "@/utils/queryParams";
import { usePropagatedRouter } from "./usePropagatedRouter";

const PARTNER_INTENT_KEY = "partner_intent";

function isValidPartnerIntent(value: string): value is PartnerIntent {
  return Object.values(PartnerIntent).includes(value as PartnerIntent);
}

export function resolvePartnerIntent(value: string): PartnerIntent | undefined {
  if (isValidPartnerIntent(value)) return value;
  return Object.values(PartnerIntent)
    .sort((a, b) => b.length - a.length)
    .find((partner) => value.startsWith(`${partner}_`));
}

/** Gets the partner from the url e.g `/sams-club/urgent-care`, `/walmart/glp-1-program` */
export function partnerFromPathname(
  pathname: string | undefined,
): PartnerIntent | undefined {
  const slug = pathname?.split("/")[1]?.replace(/-/g, "_");
  return slug ? resolvePartnerIntent(slug) : undefined;
}

function partnerFromParams({
  partner,
  utmSource,
}: {
  partner?: QueryParam;
  utmSource?: QueryParam;
}): PartnerIntent | undefined {
  const partnerParam = firstQueryParam(partner);
  // Only valid partners are returned, and can be resolved from both partner or utm_source
  return (
    resolvePartnerIntent(partnerParam ?? "") ??
    resolvePartnerIntent(firstQueryParam(utmSource) ?? "")
  );
}

export function usePartnerIntent() {
  const { partner: partnerParam, utm_source: utmSourceParam } =
    useGlobalSearchParams<{
      partner: QueryParam;
      utm_source: QueryParam;
    }>();
  const pathname = usePathname();

  /** Falls back to partner in query param if storage is empty (unavailable localstorage) */
  const paramPartner = useMemo(
    () =>
      partnerFromParams({
        partner: partnerParam,
        utmSource: utmSourceParam,
      }),
    [partnerParam, utmSourceParam],
  );

  const getLocalPartnerIntent = useCallback(async (): Promise<
    PartnerIntentRequest | undefined
  > => {
    let stored = await asyncStorage.get<PartnerIntentRequest | string>(
      PARTNER_INTENT_KEY,
    );

    // Handle backwards compatibility: old format was just a string
    if (typeof stored === "string") {
      stored = isValidPartnerIntent(stored) ? { partner: stored } : undefined;
    }

    const routePartner = partnerFromPathname(pathname);
    if (routePartner && stored?.partner !== routePartner) {
      return { partner: routePartner };
    }
    if (stored) return stored;

    return paramPartner ? { partner: paramPartner } : undefined;
  }, [paramPartner, pathname]);

  return { getLocalPartnerIntent };
}

export const clearPartnerIntent = () => asyncStorage.remove(PARTNER_INTENT_KEY);

/**
 * Resolves the partner attribution in priority order from url pathname, then `partner` or `utm_source` query params
 * Valid partners are added to localstorage and query params for backup
 */
export function useCapturePartnerIntent() {
  const params = useGlobalSearchParams<Record<string, QueryParam>>();
  const { partner: partnerParam, utm_source: utmSource } = params;
  const pathname = usePathname();
  const router = usePropagatedRouter();
  const routePartner = partnerFromPathname(pathname);
  const partner =
    routePartner ?? partnerFromParams({ partner: partnerParam, utmSource });

  useEffect(() => {
    if (partner) {
      asyncStorage.set(PARTNER_INTENT_KEY, {
        partner,
      } satisfies PartnerIntentRequest);
      addBreadcrumb({
        category: "partner-intent",
        level: "info",
        message: `Stored partner intent — partner=${partner}`,
      });
      return;
    }
    const ignored = firstQueryParam(partnerParam);
    if (ignored) {
      captureMessage(
        `Ignored invalid partner intent from URL param: partner=${ignored}`,
      );
    }
  }, [partner, partnerParam]);

  useEffect(() => {
    // replace() queues until navigation is ready; setParams() dispatches straight
    // into React Navigation and is discarded by the route's initial hydration.
    if (!routePartner || firstQueryParam(partnerParam) === routePartner) return;
    router.replace({
      pathname,
      params: { ...params, partner: routePartner },
    } as Href);
  }, [routePartner, partnerParam, params, pathname, router]);
}
